import React, { useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { codigoBarrasService, CodigoBarrasInformacoes, CodigoBarrasRequest } from '../../services/codigoBarras';

interface CodigoBarrasFormProps {
  isEdit?: boolean;
}

const CodigoBarrasForm: React.FC<CodigoBarrasFormProps> = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = React.useState({
    referenciaInformada: '',
    valorInformado: '',
    tamanho: '',
    quantidade: '',
    descricao: '',
  });
  
  const [errors, setErrors] = React.useState({
    referenciaInformada: '',
    valorInformado: '',
    tamanho: '',
    quantidade: '',
    descricao: '',
  });

  // Buscar código de barras para edição
  const { data: codigoBarras, isLoading } = useQuery({
    queryKey: ['codigoBarras', id],
    queryFn: () => codigoBarrasService.getAll().then(res => res.find(c => c.id === Number(id))),
    enabled: isEdit && !!id,
  });

  // Mutation para salvar
  const saveMutation = useMutation({
    mutationFn: (data: CodigoBarrasRequest) => codigoBarrasService.save(data),
    onSuccess: () => {
      // Invalidar o cache para forçar atualização
      queryClient.invalidateQueries({ queryKey: ['codigoBarras'] });
      navigate('/codigoBarras');
    },
    onError: (error: any) => {
      console.error('Erro ao salvar código de barras:', error);
    },
  });

  useEffect(() => {
    if (codigoBarras && isEdit) {
      setFormData({
        referenciaInformada: codigoBarras.referenciaInformada,
        valorInformado: codigoBarras.valorInformado,
        tamanho: codigoBarras.tamanho,
        quantidade: codigoBarras.quantidade.toString(),
        descricao: codigoBarras.descricao,
      });
    }
  }, [codigoBarras, isEdit]);

  const validateForm = (): boolean => {
    const newErrors = {
      referenciaInformada: '',
      valorInformado: '',
      tamanho: '',
      quantidade: '',
      descricao: '',
    };
    let isValid = true;

    // Validação Referência
    if (!formData.referenciaInformada.trim()) {
      newErrors.referenciaInformada = 'Referência é obrigatória';
      isValid = false;
    } else if (formData.referenciaInformada.length > 4) {
      newErrors.referenciaInformada = 'Referência não deve possuir mais que 4 caracteres';
      isValid = false;
    }

    // Validação Quantidade
    if (!formData.quantidade.trim()) {
      newErrors.quantidade = 'Quantidade é obrigatória';
      isValid = false;
    } else if (isNaN(Number(formData.quantidade)) || Number(formData.quantidade) <= 0) {
      newErrors.quantidade = 'Quantidade deve ser um número maior que 0';
      isValid = false;
    }

    // Validação Valor
    if (formData.valorInformado.length > 5) {
      newErrors.valorInformado = 'Valor não deve possuir mais que 5 caracteres';
      isValid = false;
    }

    // Validação Tamanho
    if (formData.tamanho.length > 5) {
      newErrors.tamanho = 'Tamanho não deve possuir mais que 5 caracteres';
      isValid = false;
    }

    // Validação Descrição
    if (formData.descricao.length > 100) {
      newErrors.descricao = 'Descrição não deve possuir mais que 100 caracteres';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      const codigoBarrasData: CodigoBarrasRequest = {
        id: isEdit ? Number(id) : undefined,
        referenciaInformada: formData.referenciaInformada.trim(),
        valorInformado: formData.valorInformado.trim(),
        tamanho: formData.tamanho.trim(),
        quantidade: Number(formData.quantidade),
        descricao: formData.descricao.trim(),
      };
      
      saveMutation.mutate(codigoBarrasData);
    }
  };

  const handleClear = () => {
    setFormData({
      referenciaInformada: '',
      valorInformado: '',
      tamanho: '',
      quantidade: '',
      descricao: '',
    });
    setErrors({
      referenciaInformada: '',
      valorInformado: '',
      tamanho: '',
      quantidade: '',
      descricao: '',
    });
  };

  const handleBack = () => {
    navigate('/codigoBarras');
  };

  const handleInputChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
    
    // Limpar erro do campo quando usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  if (isEdit && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          <Box>
            <TextField
              fullWidth
              label="Referência"
              value={formData.referenciaInformada}
              onChange={handleInputChange('referenciaInformada')}
              error={!!errors.referenciaInformada}
              helperText={errors.referenciaInformada}
              slotProps={{
                htmlInput: { maxLength: 4, name: 'referencia', autoComplete: 'on' }
              }}
              required
            />
          </Box>
          
          <Box>
            <TextField
              fullWidth
              label="Valor"
              value={formData.valorInformado}
              onChange={handleInputChange('valorInformado')}
              error={!!errors.valorInformado}
              helperText={errors.valorInformado}
              slotProps={{
                htmlInput: { maxLength: 5, name: 'valor', autoComplete: 'on' }
              }}
            />
          </Box>
          
          <Box>
            <TextField
              fullWidth
              label="Quantidade"
              type="number"
              value={formData.quantidade}
              onChange={handleInputChange('quantidade')}
              error={!!errors.quantidade}
              helperText={errors.quantidade}
              slotProps={{
                htmlInput: { name: 'quantidade', autoComplete: 'on' }
              }}
              required
            />
          </Box>
          
          <Box>
            <TextField
              fullWidth
              label="Tamanho"
              value={formData.tamanho}
              onChange={handleInputChange('tamanho')}
              error={!!errors.tamanho}
              helperText={errors.tamanho}
              slotProps={{
                htmlInput: { maxLength: 5, name: 'tamanho', autoComplete: 'on' }
              }}
            />
          </Box>
        </Box>
        
        <Box sx={{ mt: 3 }}>
          <TextField
            fullWidth
            label="Descrição"
            value={formData.descricao}
            onChange={handleInputChange('descricao')}
            error={!!errors.descricao}
            helperText={errors.descricao}
            slotProps={{
              htmlInput: { maxLength: 100, name: 'descricao', autoComplete: 'on' }
            }}
            multiline
            rows={3}
          />
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saveMutation.isPending}
            sx={{ minWidth: 120 }}
          >
            {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
          
          <Button
            type="button"
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleClear}
            sx={{ minWidth: 120 }}
          >
            Limpar
          </Button>
          
          <Button
            type="button"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ minWidth: 120 }}
          >
            Voltar
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default CodigoBarrasForm;
